import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ROOM_OPEN_OFFSETS_MIN, type FamilyRole, type RoomAdultCandidate, type RoomAdultView, type RoomView } from '@kidzim/shared';
import { Badge, Button, Card, Input, Switch } from '../design-system/components';
import { api } from '../lib/api';
import { RoomStatusBadge } from './Overview';

const offsetLabels: Record<number, string> = { 0: 'Now', 5: 'In 5 min', 15: 'In 15 min', 30: 'In 30 min' };

const FAMILY_ROLE_LABEL: Record<FamilyRole, string> = {
  owner: 'Owner',
  co_parent: 'Co-parent',
  trusted_adult: 'Trusted adult',
};

interface TranscriptMessage {
  id: string;
  authorName: string;
  body: string;
  /** What the kid actually sent, before moderation removed anything. Present
   *  only on moderated messages; for parent review only. */
  originalBody: string | null;
  moderation: 'clean' | 'link_blocked' | 'image_blocked' | 'flagged';
  createdAt: string;
}
interface Invite {
  id: string;
  screenName: string;
  parentEmail: string | null;
  kidId: string | null;
  canMagicLink: boolean;
  redeemedAt: string | null;
}

export function RoomDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState<RoomView | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [adults, setAdults] = useState<RoomAdultView[]>([]);
  const [candidates, setCandidates] = useState<RoomAdultCandidate[]>([]);
  // Reschedule (reopen / extend) form state.
  const [showSchedule, setShowSchedule] = useState(false);
  const [permanent, setPermanent] = useState(false);
  const [openInMinutes, setOpenInMinutes] = useState(0);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [magicMsg, setMagicMsg] = useState<{ kidId: string; text: string } | null>(null);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [emailSent, setEmailSent] = useState(false);

  function toggleReveal(id: string) {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const loadTranscript = useCallback(async () => {
    const t = await api<{ room: RoomView; messages: TranscriptMessage[] }>(`/api/rooms/${id}/transcript`);
    setRoom(t.room);
    setMessages(t.messages);
  }, [id]);

  const loadAdults = useCallback(async () => {
    const a = await api<{ adults: RoomAdultView[]; candidates: RoomAdultCandidate[] }>(`/api/rooms/${id}/adults`);
    setAdults(a.adults);
    setCandidates(a.candidates);
  }, [id]);

  useEffect(() => {
    api<{ room: RoomView; invites: Invite[] }>(`/api/rooms/${id}`).then((r) => {
      setRoom(r.room);
      setInvites(r.invites);
    });
    loadTranscript().catch(() => undefined);
    loadAdults().catch(() => undefined);
  }, [id, loadTranscript, loadAdults]);

  async function addAdult(userId: string) {
    const a = await api<{ adults: RoomAdultView[]; candidates: RoomAdultCandidate[] }>(`/api/rooms/${id}/adults`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
    setAdults(a.adults);
    setCandidates(a.candidates);
  }

  async function removeAdult(userId: string) {
    const a = await api<{ adults: RoomAdultView[]; candidates: RoomAdultCandidate[] }>(`/api/rooms/${id}/adults/${userId}`, {
      method: 'DELETE',
    });
    setAdults(a.adults);
    setCandidates(a.candidates);
  }

  async function closeRoom() {
    const r = await api<{ room: RoomView }>(`/api/rooms/${id}/close`, { method: 'POST' });
    setRoom(r.room);
  }

  async function reschedule() {
    setBusy(true);
    try {
      const body = permanent
        ? { permanent: true }
        : { permanent: false, openInMinutes, durationMinutes };
      const r = await api<{ room: RoomView }>(`/api/rooms/${id}/reschedule`, { method: 'POST', body: JSON.stringify(body) });
      setRoom(r.room);
      setShowSchedule(false);
    } finally {
      setBusy(false);
    }
  }

  async function duplicate() {
    setBusy(true);
    try {
      const r = await api<{ room: RoomView }>(`/api/rooms/${id}/duplicate`, { method: 'POST', body: JSON.stringify({}) });
      navigate(`/app/rooms/${r.room.id}`);
    } finally {
      setBusy(false);
    }
  }

  async function copyMagicLink(kidId: string) {
    setMagicMsg(null);
    try {
      const { url } = await api<{ url: string }>(`/api/rooms/${id}/magic-link`, {
        method: 'POST',
        body: JSON.stringify({ kidId }),
      });
      await navigator.clipboard?.writeText(url);
      setMagicMsg({ kidId, text: 'Sign-in link copied! Paste it on your kid’s device.' });
    } catch {
      setMagicMsg({ kidId, text: 'Could not create a sign-in link for this kid.' });
    }
  }

  async function archive() {
    if (!window.confirm('Archive this room? It will close and move out of your active rooms. The transcript is kept.')) return;
    const r = await api<{ room: RoomView }>(`/api/rooms/${id}/archive`, { method: 'POST' });
    setRoom(r.room);
  }

  async function unarchive() {
    const r = await api<{ room: RoomView }>(`/api/rooms/${id}/unarchive`, { method: 'POST' });
    setRoom(r.room);
  }

  async function clearTranscript() {
    if (!window.confirm('Permanently delete every message in this room? This can\'t be undone.')) return;
    await api(`/api/rooms/${id}/transcript/clear`, { method: 'POST' });
    setMessages([]);
  }

  async function emailTranscript() {
    await api(`/api/rooms/${id}/transcript/email`, { method: 'POST' });
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  }

  if (!room) return <p className="kz-muted">Loading…</p>;

  return (
    <div className="kz-stack" style={{ gap: 'var(--space-6)' }}>
      <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <div>
          <span className="kz-eyebrow">Chat room</span>
          <h1 className="kz-row" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-1)' }}>
            {room.name} <RoomStatusBadge status={room.status} />
            {room.archived && <Badge tone="neutral">Archived</Badge>}
          </h1>
        </div>
        <div className="kz-row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {!room.archived && room.status !== 'closed' && (
            <Link to={`/chat/${room.id}`}>
              <Button variant="secondary">Open chat</Button>
            </Link>
          )}
          {!room.archived && (
            <Button variant="secondary" onClick={() => setShowSchedule((s) => !s)}>
              {room.status === 'closed' ? 'Reopen' : 'Reschedule'}
            </Button>
          )}
          <Button variant="secondary" onClick={duplicate} disabled={busy}>Duplicate</Button>
          {!room.archived && room.status !== 'closed' && (
            <Button variant="danger" onClick={closeRoom}>Close now</Button>
          )}
          {room.archived ? (
            <Button variant="secondary" onClick={unarchive}>Unarchive</Button>
          ) : (
            <Button variant="ghost" onClick={archive}>Archive</Button>
          )}
        </div>
      </div>

      {showSchedule && !room.archived && (
        <Card>
          <h3 style={{ marginBottom: 'var(--space-3)' }}>
            {room.status === 'closed' ? 'Reopen this room' : 'Reschedule this room'}
          </h3>
          <div className="kz-stack" style={{ gap: 'var(--space-4)' }}>
            <Switch label="Always open (no schedule)" checked={permanent} onChange={setPermanent} />
            {!permanent && (
              <div className="kz-row" style={{ gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                <div>
                  <span className="kz-field-label">Opens</span>
                  <div className="kz-row" style={{ gap: 'var(--space-1)' }}>
                    {ROOM_OPEN_OFFSETS_MIN.map((m) => (
                      <Button key={m} type="button" size="sm" variant={openInMinutes === m ? 'primary' : 'secondary'} onClick={() => setOpenInMinutes(m)}>
                        {offsetLabels[m]}
                      </Button>
                    ))}
                  </div>
                </div>
                <div style={{ width: 140 }}>
                  <Input label="Open for (min)" type="number" min={5} max={720} value={durationMinutes} onChange={(e) => setDurationMinutes(Number(e.target.value))} />
                </div>
              </div>
            )}
            <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
              <Button onClick={reschedule} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
              <Button variant="ghost" onClick={() => setShowSchedule(false)}>Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <h3 style={{ marginBottom: 'var(--space-1)' }}>Invited buddies</h3>
        <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-3)' }}>
          Once a kid in your family has joined, you can copy a one-tap sign-in link for them — it drops them
          straight into this chat on their device, no PIN needed.
        </p>
        <div className="kz-stack" style={{ gap: 'var(--space-2)' }}>
          {invites.map((inv) => (
            <div key={inv.id} className="kz-stack" style={{ gap: 'var(--space-1)' }}>
              <div className="kz-row" style={{ justifyContent: 'space-between' }}>
                <span><strong>{inv.screenName}</strong> {inv.parentEmail && <span className="kz-muted">· {inv.parentEmail}</span>}</span>
                <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
                  {inv.canMagicLink && inv.kidId && (
                    <Button variant="secondary" size="sm" onClick={() => copyMagicLink(inv.kidId!)}>
                      Copy sign-in link
                    </Button>
                  )}
                  {inv.redeemedAt ? <Badge tone="safe">Joined</Badge> : <Badge tone="neutral">Invited</Badge>}
                </div>
              </div>
              {magicMsg?.kidId === inv.kidId && (
                <span style={{ font: 'var(--type-small)', color: 'var(--safe-700)' }}>{magicMsg.text}</span>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h3 style={{ marginBottom: 'var(--space-1)' }}>Grown-ups who can chat</h3>
        <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-3)' }}>
          You (the host) can always chat here. Add a co-parent or trusted adult from your family so they can join in too —
          their messages show the kids a friendly “grown-up” badge.
        </p>
        <div className="kz-stack" style={{ gap: 'var(--space-2)' }}>
          {adults.length === 0 ? (
            <p className="kz-muted" style={{ font: 'var(--type-small)' }}>No other grown-ups added yet.</p>
          ) : (
            adults.map((a) => (
              <div key={a.userId} className="kz-row" style={{ justifyContent: 'space-between' }}>
                <span>
                  <strong>{a.displayName}</strong> <Badge tone="neutral">{FAMILY_ROLE_LABEL[a.familyRole]}</Badge>
                </span>
                <Button variant="ghost" size="sm" onClick={() => removeAdult(a.userId)}>Remove</Button>
              </div>
            ))
          )}
        </div>
        {candidates.length > 0 && (
          <div className="kz-stack" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            <span className="kz-field-label">Add a grown-up</span>
            {candidates.map((c) => (
              <div key={c.userId} className="kz-row" style={{ justifyContent: 'space-between' }}>
                <span>{c.displayName} <span className="kz-muted">· {FAMILY_ROLE_LABEL[c.familyRole]}</span></span>
                <Button variant="secondary" size="sm" onClick={() => addAdult(c.userId)}>Add</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="kz-row" style={{ justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
          <h3>Transcript</h3>
          <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
            <Button variant="secondary" size="sm" onClick={() => loadTranscript()}>Refresh</Button>
            <Button variant="secondary" size="sm" onClick={emailTranscript}>{emailSent ? 'Sent ✓' : 'Email me this'}</Button>
            {messages.length > 0 && (
              <Button variant="ghost" size="sm" style={{ color: 'var(--flag-500)' }} onClick={clearTranscript}>Clear</Button>
            )}
          </div>
        </div>
        {messages.length === 0 ? (
          <p className="kz-muted">No messages yet.</p>
        ) : (
          <div className="kz-stack" style={{ gap: 'var(--space-2)' }}>
            {messages.map((m) => (
              <div key={m.id} className="kz-stack" style={{ gap: 2 }}>
                <div className="kz-row" style={{ gap: 'var(--space-3)', alignItems: 'baseline' }}>
                  <span className="kz-mono" style={{ fontSize: 'var(--text-2xs)', color: 'var(--ink-400)', minWidth: 64 }}>
                    {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span style={{ font: 'var(--type-label)', color: 'var(--blue-600)', minWidth: 90 }}>{m.authorName}</span>
                  <span className="kz-grow">{m.body}</span>
                  {m.moderation !== 'clean' && (
                    <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
                      <Badge tone={m.moderation === 'flagged' ? 'flag' : 'warn'}>{m.moderation.replace('_', ' ')}</Badge>
                      {m.originalBody && (
                        <Button variant="ghost" size="sm" onClick={() => toggleReveal(m.id)}>
                          {revealed.has(m.id) ? 'Hide' : 'Show removed'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {m.originalBody && revealed.has(m.id) && (
                  <div
                    style={{
                      marginLeft: 76,
                      padding: '6px 10px',
                      background: 'var(--flag-100)',
                      border: 'var(--border-width) solid var(--flag-100)',
                      borderRadius: 'var(--radius-sm)',
                      font: 'var(--type-small)',
                      wordBreak: 'break-word',
                    }}
                  >
                    <span className="kz-muted">What {m.authorName} actually sent: </span>
                    <span style={{ color: 'var(--ink-900)' }}>{m.originalBody}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
